/**
 * Accounting Service - Real-time balance subscription and user impact tracking
 */

import { db } from '../firebase/config';
import { doc, onSnapshot, collection, query, where, orderBy, limit, Timestamp } from 'firebase/firestore';

export interface SystemAccounting {
  openRouterBalance: number;
  virtualStripePool: number;
  balanceAdjustment: number; // Fixed offset for display balance
  paypalPledged: number; // Honor-system PayPal pledges
  lastBalanceCheck: Timestamp;
  lastBalanceFetch: number;
  totalContributions: number;
  totalSpent: number;
  totalRefills: number;
  alertThreshold: number;
  activeUserCount: number; // Users with at least one active conversation
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface BalanceHistoryPoint {
  timestamp: Timestamp;
  displayBalance: number;
  openRouterBalance: number;
  virtualStripePool: number;
  active: boolean;
}

export interface UserAccountingStats {
  totalContributions: number;
  contributionCount: number;
  lastContributionAt?: Timestamp;
  totalCosts: number;
  impact: number;
  lastCostUpdate: Timestamp;
}

export interface BalanceData {
  displayBalance: number;
  openRouterBalance: number;
  virtualStripePool: number;
  isLow: boolean;
  userImpact: number;
  sparklineData: BalanceHistoryPoint[];
  activeUserCount: number;
}

/**
 * Subscribe to system balance with real-time updates
 */
export function subscribeToSystemBalance(
  userId: string | undefined,
  onUpdate: (data: BalanceData) => void,
  onError?: (error: Error) => void
): () => void {
  const unsubscribers: Array<() => void> = [];

  // State to accumulate data
  let systemData: SystemAccounting | null = null;
  let userStats: UserAccountingStats | null = null;
  let historyData: BalanceHistoryPoint[] = [];

  const emitUpdate = () => {
    if (!systemData) return;

    // displayBalance = openRouterBalance + virtualStripePool + balanceAdjustment + paypalPledged
    // balanceAdjustment can be negative to reconcile pledges vs actual receipts
    const adjustment = systemData.balanceAdjustment || 0;
    const pledged = systemData.paypalPledged || 0;
    const displayBalance = systemData.openRouterBalance + systemData.virtualStripePool + adjustment + pledged;
    const isLow = displayBalance <= (systemData.alertThreshold || 10);
    const userImpact = userStats?.impact || 0;

    onUpdate({
      displayBalance,
      openRouterBalance: systemData.openRouterBalance,
      virtualStripePool: systemData.virtualStripePool,
      isLow,
      userImpact,
      sparklineData: historyData,
      activeUserCount: systemData.activeUserCount || 0,
    });
  };

  // Subscribe to system accounting document
  const systemDocRef = doc(db, 'system', 'accounting');
  const unsubSystem = onSnapshot(
    systemDocRef,
    (snapshot) => {
      if (snapshot.exists()) {
        systemData = snapshot.data() as SystemAccounting;
        emitUpdate();
      }
    },
    (error) => {
      console.error('Error subscribing to system accounting:', error);
      onError?.(error);
    }
  );
  unsubscribers.push(unsubSystem);

  // Subscribe to user stats (if userId provided)
  if (userId) {
    const userStatsRef = doc(db, 'users', userId, 'accounting', 'stats');
    const unsubUser = onSnapshot(
      userStatsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          userStats = snapshot.data() as UserAccountingStats;
          emitUpdate();
        } else {
          // User has no stats yet (never contributed or used)
          userStats = {
            totalContributions: 0,
            contributionCount: 0,
            totalCosts: 0,
            impact: 0,
            lastCostUpdate: Timestamp.now(),
          };
          emitUpdate();
        }
      },
      (error) => {
        console.error('Error subscribing to user stats:', error);
        onError?.(error);
      }
    );
    unsubscribers.push(unsubUser);
  }

  // Subscribe to balance history (last 30 days for sparkline, active only)
  // Requires composite index: active ASC, timestamp ASC
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const historyRef = collection(db, 'system', 'accounting', 'history');
  const historyQuery = query(
    historyRef,
    where('active', '==', true),
    where('timestamp', '>=', Timestamp.fromDate(thirtyDaysAgo)),
    orderBy('timestamp', 'asc'),
    limit(200)
  );

  const unsubHistory = onSnapshot(
    historyQuery,
    (snapshot) => {
      historyData = snapshot.docs.map(doc => doc.data() as BalanceHistoryPoint);
      emitUpdate();
    },
    (error) => {
      console.error('Error subscribing to balance history:', error);
      onError?.(error);
    }
  );
  unsubscribers.push(unsubHistory);

  // Return cleanup function
  return () => {
    unsubscribers.forEach(unsub => unsub());
  };
}

/**
 * Format currency with proper sign
 * Shows meaningful precision: 2-6 decimals depending on amount
 */
export function formatCurrency(amount: number, showSign = false): string {
  const absAmount = Math.abs(amount);
  
  // Always show at least 2 decimals, up to 6 for precision
  // Remove trailing zeros for cleaner display
  let formatted: string;
  if (absAmount === 0) {
    formatted = '0.00';
  } else if (absAmount < 0.01) {
    // Micro amounts: show up to 6 decimals
    formatted = absAmount.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
  } else if (absAmount < 100) {
    // Small amounts: show up to 4 decimals for precision
    formatted = absAmount.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
  } else {
    // Large amounts: show up to 2 decimals
    formatted = absAmount.toFixed(2);
  }
  
  if (showSign && amount >= 0) {
    return `+$${formatted}`;
  } else if (amount < 0) {
    return `-$${formatted}`;
  }
  return `$${formatted}`;
}

